import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCategories,
  getClientPurchaseOrderCategories,
  getClientQuotationCategories,
  updateClientPurchaseOrderCategories,
  updateClientQuotationCategories,
} from '../api';
import { normalizePaginatedResponse } from '../utils/pagination';

const normalizeIdList = (value) => {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const ids = [];
  value.forEach((entry) => {
    const id = Number(entry);
    if (!Number.isInteger(id) || id <= 0 || seen.has(id)) return;
    ids.push(String(id));
    seen.add(id);
  });
  return ids;
};

const API_BY_SCOPE = {
  quotation: {
    getAssigned: getClientQuotationCategories,
    updateAssigned: updateClientQuotationCategories,
    loadError: 'No se pudieron cargar las categorias del portal.',
    saveError: 'No se pudieron guardar las categorias del portal.',
  },
  purchaseOrder: {
    getAssigned: getClientPurchaseOrderCategories,
    updateAssigned: updateClientPurchaseOrderCategories,
    loadError: 'No se pudieron cargar las categorias de ordenes de compra.',
    saveError: 'No se pudieron guardar las categorias de ordenes de compra.',
  },
};

const useClientQuotationCategories = (clientId, enabled, scope = 'quotation') => {
  const api = API_BY_SCOPE[scope] || API_BY_SCOPE.quotation;
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryIds, setCategoryIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const loadRequestRef = useRef(0);

  const loadCategories = useCallback(async () => {
    if (!enabled || !clientId) {
      loadRequestRef.current += 1;
      setCategoryOptions([]);
      setCategoryIds([]);
      setLoaded(false);
      setError('');
      return;
    }

    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    setLoading(true);
    setLoaded(false);
    setError('');
    try {
      const [categoriesResponse, assignedResponse] = await Promise.all([
        getCategories(),
        api.getAssigned(clientId),
      ]);
      const categories = Array.isArray(categoriesResponse)
        ? categoriesResponse
        : normalizePaginatedResponse(categoriesResponse).data;
      if (loadRequestRef.current !== requestId) return;
      setCategoryOptions(
        categories
          .filter((category) => category?.isActive !== false)
          .map((category) => ({
            id: String(category.id),
            name: category.name || `Categoria ${category.id}`,
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setCategoryIds(normalizeIdList(assignedResponse?.categoryIds));
      setLoaded(true);
    } catch (err) {
      if (loadRequestRef.current !== requestId) return;
      setCategoryOptions([]);
      setCategoryIds([]);
      setError(err?.response?.data?.message || api.loadError);
    } finally {
      if (loadRequestRef.current === requestId) setLoading(false);
    }
  }, [api, clientId, enabled]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const selectedCategoryIds = useMemo(
    () => new Set(categoryIds.map(String)),
    [categoryIds]
  );

  const toggleCategory = (categoryId) => {
    const id = String(categoryId);
    setCategoryIds((prev) => {
      const current = new Set(prev.map(String));
      if (current.has(id)) {
        current.delete(id);
      } else {
        current.add(id);
      }
      return Array.from(current);
    });
    setError('');
  };

  const saveCategoryIds = async () => {
    if (!clientId || !enabled || !loaded) return null;
    setSaving(true);
    setError('');
    try {
      const payloadIds = categoryIds
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id));
      const response = await api.updateAssigned(clientId, payloadIds);
      setCategoryIds(normalizeIdList(response?.categoryIds));
      return response;
    } catch (err) {
      setError(err?.response?.data?.message || api.saveError);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return {
    categoryOptions,
    categoryIds,
    selectedCategoryIds,
    loading,
    saving,
    loaded,
    error,
    setError,
    toggleCategory,
    reload: loadCategories,
    saveCategoryIds,
  };
};

export default useClientQuotationCategories;
